import { useEffect, useState, useCallback } from "react";
import { Smartphone, X, Share, Plus, MoreVertical, Download } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

const detectPlatform = () => {
  const ua = window.navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua) ||
    (window.navigator.platform === "MacIntel" && (window.navigator.maxTouchPoints || 0) > 1);
  const android = /android/i.test(ua);
  return { iOS, android };
};

// Pulsante "Aggiungi a Home": usa il prompt nativo su Android/Chrome/Edge;
// su iOS mostra le istruzioni (Safari non espone un prompt programmatico).
export const AddToHomeButton = ({ className = "" }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showHelp, setShowHelp] = useState(false);
  const { iOS, android } = detectPlatform();

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setShowHelp(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const onClick = useCallback(async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* noop */ }
      setDeferred(null);
      return;
    }
    setShowHelp(true);
  }, [deferred]);

  if (installed) return null;
  // Su desktop senza prompt nativo non ha senso: mostriamo il pulsante solo se
  // c'è il prompt (Android/desktop installabile) o se siamo su iOS (istruzioni).
  if (!deferred && !iOS && !android) return null;

  return (
    <>
      <button
        type="button"
        data-testid="add-to-home-btn"
        onClick={onClick}
        className={`inline-flex items-center gap-1 text-[11px] font-medium text-[#007BFF] hover:underline ${className}`}
        title="Aggiungi il portale alla schermata Home"
      >
        <Smartphone className="h-3.5 w-3.5" />Aggiungi a Home
      </button>

      {showHelp && (
        <div className="fixed inset-0 z-[220] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="a2hs-help" onClick={() => setShowHelp(false)}>
          <div className="w-full max-w-sm bg-card border border-border rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-head text-lg font-bold flex items-center gap-2">
                <Download className="h-5 w-5 text-[#007BFF]" />Aggiungi a Home
              </h3>
              <button data-testid="a2hs-help-close" onClick={() => setShowHelp(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            {iOS ? (
              <ol className="text-sm text-muted-foreground space-y-2 leading-relaxed">
                <li className="flex items-start gap-2"><span className="font-bold text-foreground">1.</span><span>Tocca l'icona <Share className="inline h-4 w-4 -mt-0.5 text-[#007BFF]" /> <strong>Condividi</strong> in basso (Safari).</span></li>
                <li className="flex items-start gap-2"><span className="font-bold text-foreground">2.</span><span>Scorri e tocca <strong>«Aggiungi alla schermata Home»</strong> <Plus className="inline h-4 w-4 -mt-0.5 text-[#007BFF]" />.</span></li>
                <li className="flex items-start gap-2"><span className="font-bold text-foreground">3.</span><span>Conferma con <strong>«Aggiungi»</strong>: troverai l'icona di Sedio tra le tue app.</span></li>
              </ol>
            ) : (
              <ol className="text-sm text-muted-foreground space-y-2 leading-relaxed">
                <li className="flex items-start gap-2"><span className="font-bold text-foreground">1.</span><span>Apri il menu <MoreVertical className="inline h-4 w-4 -mt-0.5 text-[#007BFF]" /> del browser (in alto a destra).</span></li>
                <li className="flex items-start gap-2"><span className="font-bold text-foreground">2.</span><span>Tocca <strong>«Installa app»</strong> oppure <strong>«Aggiungi a schermata Home»</strong>.</span></li>
                <li className="flex items-start gap-2"><span className="font-bold text-foreground">3.</span><span>Conferma: troverai l'icona di Sedio nella Home.</span></li>
              </ol>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AddToHomeButton;
